/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { startOfMonth } from '@internationalized/date';

import {
    Button,
    Alert,
    Stack,
    Text,
    Icon,
    Box,
    STATUS,
    localToday,
    Spinner,
} from 'sarsaparilla';
import { calendarDateToMoment, momentToCalendarDate } from 'sarsaparilla/moment-utils';

import DatePicker from '../../components/common/DatePicker';
import {
    fetchTourAvailability,
    resetTourAvailability,
} from '../../actions/tourAvailabilityActions';
import { setDateFilter } from '../../actions/filtersActions';

export function ModifyCalendarContainer() {
    const dispatch = useDispatch();

    const facilityId = useSelector((state) => state.timedEntry.facility?.facility_id);
    const reduxOrderTourId = useSelector(
        (state) => state.timedEntry.orderDetails?.tour_id
    );
    const date = useSelector((state) => state.timedEntry.filters.date);
    const availabilityData = useSelector((state) => state.timedEntry.tourAvailability);

    const reduxOrderBaseTourId =
        useSelector(
            (state) => state.timedEntry.tours?.tourMap?.[reduxOrderTourId]?.base_tour_id
        ) || '';

    const numberOfMonths = 1;
    const tourId = reduxOrderBaseTourId || reduxOrderTourId;

    const {
        isAvailableByDate,
        isNotReleasedByDate,
        isSoldOutByDate,
        isFirstComeFirstServedByDate,
        lastFetched,
        error: apiError,
        loadingStatus,
        tourId: reduxTourId,
        farthestAvailability: {
            farthestByTour,
            lastFetched: farthestAvailabilityLastFetched,
        },
    } = availabilityData;

    const hasAvailabilityData = useMemo(() => {
        return (
            (loadingStatus === STATUS.SUCCESS ||
                (Object.values(isAvailableByDate).length > 0 &&
                    (!date || isAvailableByDate?.[date?.format('YYYY-MM')]))) &&
            !!Object.keys(lastFetched)?.length
        );
    }, [date, isAvailableByDate, lastFetched, loadingStatus]);

    const lastReservableDate = useMemo(() => {
        let lastResDate;

        if (farthestByTour?.[tourId]) {
            lastResDate = moment(farthestByTour?.[tourId], 'YYYY-MM-DD');
        }

        if (!lastResDate || lastResDate?.isBefore(moment())) {
            lastResDate = moment().clone().add(1, 'M');
        }

        return lastResDate?.endOf('M');
    }, [farthestByTour, tourId]);

    const maxValue = useMemo(() => {
        return moment.isMoment(lastReservableDate) && lastReservableDate.isValid()
            ? momentToCalendarDate(lastReservableDate)
            : null;
    }, [lastReservableDate]);

    /**
     * Load availability data
     * If there is no redux data OR the tourId in the availability data doesn't match the URL,
     * we fetch all the data. ALso have to check if the redux availability data tourId is
     * the baseId to make Pearl Harbor work
     */
    useEffect(() => {
        if (tourId !== reduxTourId) {
            dispatch(resetTourAvailability());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxTourId, tourId]);

    useEffect(() => {
        if (facilityId && tourId) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: moment.isMoment(date) ? date : moment(),
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, facilityId, tourId]);

    const onDateChange = (d) => {
        dispatch(setDateFilter(d));
    };

    const handleDateChange = (value, validation) => {
        if (value && !validation.isInvalid) {
            onDateChange(calendarDateToMoment(value));
        } else {
            onDateChange(null);
        }
    };

    // On calendar month change pre-load the adjacent months (previous & next)
    // to the month being switched to
    const onVisibleRangeChange = ({ start }) => {
        const nextMonth = calendarDateToMoment(start.add({ months: 1 }));
        const prevMonth = start.subtract({ months: 1 });

        // if availability exists for the next month fetch it
        if (nextMonth?.isSameOrBefore(lastReservableDate)) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: nextMonth,
                })
            );
        }

        // if availability exists for the previous month fetch it
        if (prevMonth?.compare(startOfMonth(localToday)) >= 0) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: calendarDateToMoment(prevMonth),
                })
            );
        }
    };

    const getDayType = useCallback(
        (day) => {
            if (!hasAvailabilityData) {
                return 'loading';
            }

            if (day && localToday?.compare(day) <= 0) {
                const formattedDay = day.toString();
                const monthKey = formattedDay.slice(0, -3);
                const countForThisDay = isAvailableByDate?.[monthKey]?.[formattedDay];
                const notReleasedCountForThisDay =
                    isNotReleasedByDate?.[monthKey]?.[formattedDay];
                const soldOutForThisDay = isSoldOutByDate?.[monthKey]?.[formattedDay];
                const firstComeFirstServedForThisDay =
                    isFirstComeFirstServedByDate?.[monthKey]?.[formattedDay];

                if (
                    countForThisDay === undefined ||
                    notReleasedCountForThisDay === undefined
                ) {
                    return 'loading';
                }

                if (countForThisDay) {
                    return 'available';
                }

                if (notReleasedCountForThisDay) {
                    return 'notYetReleased';
                }

                if (firstComeFirstServedForThisDay) {
                    return 'firstComeFirstServed';
                }

                if (soldOutForThisDay) {
                    return 'soldOut';
                }
            }

            return 'blocked';
        },
        [
            hasAvailabilityData,
            isAvailableByDate,
            isFirstComeFirstServedByDate,
            isNotReleasedByDate,
            isSoldOutByDate,
        ]
    );

    const isDateUnavailable = useCallback(
        (day) => {
            if (getDayType(day) === 'blocked' || getDayType(day) === 'soldOut') {
                return true;
            }

            return false;
        },
        [getDayType]
    );

    /**
     * getTooltipContent is deprecated because it is only available to sighted users on
     * desktop using a mouse. Leaving this here in case some PM decides they want the information
     * somewhere else
     */
    // const getTooltipContent = (day) => {
    //     if (day && !isEmpty(isAvailableByDate) && !isEmpty(lastFetched)) {
    //         const formattedDay = day.format('YYYY-MM-DD');
    //         const monthKey = day.format('YYYY-MM');
    //         const countForThisDay = isAvailableByDate?.[monthKey]?.[formattedDay];
    //         const notReleasedCountForThisDay =
    //             isNotReleasedByDate?.[monthKey]?.[formattedDay];

    //         if (
    //             !countForThisDay &&
    //             !!notReleasedCountForThisDay &&
    //             nextReleaseByDate?.[monthKey]?.[formattedDay]
    //         ) {
    //             const releaseDateAndTime = moment(
    //                 nextReleaseByDate?.[monthKey]?.[formattedDay],
    //                 'YYYY-MM-DDTHHmm'
    //             );
    //             return `Next Release: ${releaseDateAndTime.format(
    //                 'll'
    //             )} at ${releaseDateAndTime.format('hh:mm a')} ${releaseDateAndTime
    //                 .tz(timezone)
    //                 .format('z')}`;
    //         }
    //     }
    //     return false;
    // };

    const tryFetchAvailabilityAgain = () => {
        if (facilityId && tourId) {
            dispatch(
                fetchTourAvailability({
                    facilityId,
                    tourId,
                    date: moment(),
                })
            );
        }
    };

    if (apiError) {
        return (
            <Alert type="error" heading="Network error" shouldFocusOnMount>
                <Stack space="sm">
                    <Text>There was a problem getting the tour availability data.</Text>

                    <Box alignX="right">
                        <Button
                            size="xs"
                            appearance="tertiary"
                            iconBeforeElement={<Icon iconName="refresh" />}
                            isLoading={loadingStatus === STATUS.PENDING}
                            onClick={tryFetchAvailabilityAgain}
                        >
                            Try again
                        </Button>
                    </Box>
                </Stack>
            </Alert>
        );
    }

    if (!moment.isMoment(date)) {
        return null;
    }

    if (!hasAvailabilityData || !farthestAvailabilityLastFetched) {
        return (
            <div className="te-facility-calendar-wrap">
                <div className="te-tour-calendar-loading-indicator">
                    <div>
                        <Spinner size="xs" />
                        <Text fontWeight="semibold">Fetching availability ...</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <DatePicker
            label="Date"
            defaultValue={momentToCalendarDate(date)}
            numVisibleMonths={numberOfMonths}
            minValue={localToday}
            maxValue={maxValue}
            onChange={handleDateChange}
            getDayType={getDayType}
            onVisibleRangeChange={onVisibleRangeChange}
            isDateUnavailable={isDateUnavailable}
        />
    );
}
